import type { HsSceneSettings } from '@sibe/hyperscene';
import { decodeCameraView, ViewerCameraView } from './viewer-url-settings';
import {
  DEFAULT_VIEWER_VISUAL_QUALITY_PROFILE,
  VIEWER_VISUAL_QUALITY_PROFILES,
  ViewerVisualQualityProfile,
} from './viewer-visual-quality';

/**
 * Start camera for a freshly opened scene. Precedence: explicit URL camera or viewpoint, then the scene default;
 * with neither, the viewer frames the scene bounds. Returns null when the viewer should not move the camera.
 */
export function resolveSceneStartCamera(
  urlCamera: ViewerCameraView | null,
  urlViewpointId: string | null,
  settings: HsSceneSettings | null | undefined
): ViewerCameraView | null {
  if (urlCamera !== null) {
    return urlCamera;
  }
  if (urlViewpointId !== null || settings?.startCamera === undefined || settings.startCamera === null) {
    return null;
  }
  // An unreadable stored value must not break opening the scene.
  return decodeCameraView(settings.startCamera);
}

/**
 * Quality profile to apply when a scene opens, or null to keep what the URL (or the global default) gives.
 * An explicit `qualityProfile` URL parameter always wins; unknown ids and the global default are ignored.
 */
export function resolveSceneQualityProfile(
  urlQualityProfile: string | null,
  settings: HsSceneSettings | null | undefined
): ViewerVisualQualityProfile | null {
  if (urlQualityProfile !== null) {
    return null;
  }
  const profile: string | null | undefined = settings?.qualityProfile;
  const known: ViewerVisualQualityProfile | undefined = VIEWER_VISUAL_QUALITY_PROFILES.find(
    (item: (typeof VIEWER_VISUAL_QUALITY_PROFILES)[number]) => item.value === profile
  )?.value;
  return known === undefined || known === DEFAULT_VIEWER_VISUAL_QUALITY_PROFILE ? null : known;
}
